﻿import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FileText, ExternalLink } from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import { useLegalVersions } from "../../hooks/useLegalVersions";

export function LegalUpdatePrompt() {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const { data, accept } = useLegalVersions(Boolean(user));
  const [submitting, setSubmitting] = useState(false);

  const needs = data?.user?.needs_reacceptance;
  const firstAcceptanceMissing =
    Boolean(user) && data?.user && !data.user.tos_accepted_at;

  const visible = Boolean(user) && (needs || firstAcceptanceMissing);
  if (!visible) return null;

  const handleAccept = async () => {
    setSubmitting(true);
    try {
      await accept();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/70 backdrop-blur-sm px-4">
      <div className="w-full max-w-md rounded-2xl bg-white dark:bg-slate-900 shadow-2xl ring-1 ring-slate-200 dark:ring-slate-800 p-6 sm:p-7">
        <div className="flex items-center gap-2 text-accent-600 dark:text-accent-400 text-xs font-semibold uppercase tracking-wider mb-3">
          <FileText size={14} />
          {t("legal.common.legalCenter")}
        </div>
        <h2 className="text-lg font-bold text-fg-primary mb-2">
          {t("legal.acceptance.updatedTitle")}
        </h2>
        <p className="text-sm text-fg-secondary leading-relaxed">
          {t("legal.acceptance.updatedBody")}
        </p>

        <div className="mt-4 grid grid-cols-2 gap-2">
          <Link
            to="/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800/60 text-sm text-fg-primary hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            {t("legal.acceptance.reviewTerms")}
            <ExternalLink size={14} />
          </Link>
          <Link
            to="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800/60 text-sm text-fg-primary hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            {t("legal.acceptance.reviewPrivacy")}
            <ExternalLink size={14} />
          </Link>
        </div>

        <div className="mt-6 flex items-center justify-between gap-3">
          <button
            onClick={() => logout?.()}
            className="text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
          >
            {t("legal.acceptance.logout")}
          </button>
          <button
            onClick={handleAccept}
            disabled={submitting}
            className="rounded-lg bg-accent-600 hover:bg-accent-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold px-4 py-2 text-sm shadow-md shadow-accent-500/30 transition-colors"
          >
            {submitting ? "…" : t("legal.acceptance.acceptAndContinue")}
          </button>
        </div>
      </div>
    </div>
  );
}
